import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';

interface Order {
  id: number;
  customer_name: string;
  email: string;
  address: string;
  total_price: number;
  status: string;
  date_received: string;
  date_completed?: string;
  tracking_number?: string;
  date_shipped?: string;
}

interface OrderItem {
  id: number;
  order_id: number;
  product_id: number;
  variation_id: number;
  name: string;
  quantity: number;
  price: number;
  custom_details?: string;
}

const API_BASE = process.env.REACT_APP_API_URL;

const OrderStatusPage: React.FC = () => {
  const { token } = useParams();
  const [order, setOrder] = useState<Order | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/order-status/${token}`);
        if (!res.ok) throw new Error('Invalid or expired link.');
        const orderData = await res.json();

        setOrder(orderData.order);

        setItems(orderData.items);
      } catch (err: any) {
        setError(err.message || 'Something went wrong.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [token]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 6 }}>
      <Typography variant="h4" gutterBottom>
        Order Status
      </Typography>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1">
            <strong>Order ID:</strong> {order.id}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Status:</strong> {order.status}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Date Received:</strong> {order.date_received}
          </Typography>
          {order.date_shipped && (
            <Typography variant="subtitle1">
              <strong>Date Shipped:</strong> {order.date_shipped}
            </Typography>
          )}
          {order.tracking_number && (
            <Typography variant="subtitle1">
              <strong>Tracking #:</strong> {order.tracking_number}
            </Typography>
          )}
          {order.date_completed && (
            <Typography variant="subtitle1">
              <strong>Date Completed:</strong> {order.date_completed}
            </Typography>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1">
            <strong>Customer:</strong> {order.customer_name}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Email:</strong> {order.email}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Shipping Address:</strong> {order.address}
          </Typography>
        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom>
        Items
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Product</TableCell>
            <TableCell>Quantity</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Subtotal</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                {item.name}
                {item.custom_details && (
                  <Typography variant="body2" color="text.secondary">
                    Custom: {item.custom_details}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{item.quantity}</TableCell>
              <TableCell>${item.price.toFixed(2)}</TableCell>
              <TableCell>${(item.price * item.quantity).toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Typography variant="h6">
            Total: ${typeof order.total_price === 'number' ? order.total_price.toFixed(2) : '0.00'}
        </Typography>
      </Box>
    </Container>
  );
};

export default OrderStatusPage;
